import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { toast } from 'react-toastify';
import { RCardGet } from '../../../../../../../apiRPC/card';
import { dynamicObjectSetValues } from '../../../../../../../apiRPC/dynamicObject';
import { createEffectJWT } from '../../../../../../../model/JWT';
import {
  AdditionalFieldKey,
  AdditionalFieldsBindings,
  AdditionalFieldsModel,
  buildDynamicObjectDefinition,
  recalculateFinancialFields,
} from '../model';
import { agencyDictionary, clientDictionary } from '../dictionaries/model';
import { shouldSaveDerivedField } from '../utils/shouldSaveDerivedField';

const saveAdditionalFieldsFx = createEffectJWT(dynamicObjectSetValues);

type DictionaryFieldKey = 'agencyName' | 'clientName';

export type SaveFieldOptions = {
  addToDictionary?: boolean;
  period?: Pick<AdditionalFieldsModel, 'periodStart' | 'periodEnd'>;
};

type UseAdditionalFieldsPersistenceParams = {
  card: RCardGet;
  values: AdditionalFieldsModel;
  setValues: Dispatch<SetStateAction<AdditionalFieldsModel>>;
  mainDynamicObjectId: string | null;
  canPersistDerived: boolean;
  initialValues: AdditionalFieldsModel;
  initialBindings: AdditionalFieldsBindings;
  parsedMainDynamicObjectId: string | null;
  parsedLegacyDynamicObjectIds: string[];
};

type UseAdditionalFieldsPersistenceResult = {
  saveField: (
    key: AdditionalFieldKey,
    valueOverride?: AdditionalFieldsModel[AdditionalFieldKey],
    options?: SaveFieldOptions,
  ) => Promise<void>;
  debouncedSaveField: (
    key: AdditionalFieldKey,
    value: AdditionalFieldsModel[AdditionalFieldKey],
  ) => void;
  clearSaveFieldTimeout: (key: string) => void;
  setDictionaryFieldValidity: (key: DictionaryFieldKey, valid: boolean) => void;
};

export function useAdditionalFieldsPersistence({
  card,
  values,
  setValues,
  mainDynamicObjectId,
  canPersistDerived,
  initialValues,
  initialBindings,
  parsedMainDynamicObjectId,
  parsedLegacyDynamicObjectIds,
}: UseAdditionalFieldsPersistenceParams): UseAdditionalFieldsPersistenceResult {
  const saveFieldTimeoutRef = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});
  const savesRef = useRef(
    new Map<
      AdditionalFieldKey,
      {
        value: string;
        request: Promise<unknown>;
      }
    >(),
  );

  useEffect(() => {
    savesRef.current = new Map();
    return () => {
      Object.values(saveFieldTimeoutRef.current).forEach(clearTimeout);
      saveFieldTimeoutRef.current = {};
      savesRef.current = new Map();
    };
  }, [card.uuid, mainDynamicObjectId, initialValues]);

  const validForSaveRef = useRef<Record<DictionaryFieldKey, boolean>>({
    agencyName: true,
    clientName: true,
  });
  const isFirstRecalcRunRef = useRef(true);
  const unsavedDerivedKeysRef = useRef<Set<AdditionalFieldKey>>(new Set());
  const pendingDerivedValuesRef = useRef<
    Partial<
      Record<AdditionalFieldKey, AdditionalFieldsModel[AdditionalFieldKey]>
    >
  >({});

  useEffect(() => {
    isFirstRecalcRunRef.current = true;
    unsavedDerivedKeysRef.current.clear();
    pendingDerivedValuesRef.current = {};
  }, [
    initialValues,
    initialBindings,
    parsedMainDynamicObjectId,
    parsedLegacyDynamicObjectIds,
  ]);

  const saveField = useCallback(
    async (
      key: AdditionalFieldKey,
      valueOverride?: AdditionalFieldsModel[AdditionalFieldKey],
      options?: SaveFieldOptions,
    ) => {
      clearTimeout(saveFieldTimeoutRef.current[key]);
      delete saveFieldTimeoutRef.current[key];
      const value = valueOverride !== undefined ? valueOverride : values[key];
      const definition = buildDynamicObjectDefinition(key, value);

      try {
        const targetId = mainDynamicObjectId;

        if (!targetId) {
          console.error('Попытка сохранить поле без mainDynamicObjectId');
          return;
        }

        const valuesToSave = { [key]: definition.values?.[key] };
        if (options?.period) {
          for (const dateKey of ['periodStart', 'periodEnd'] as const) {
            valuesToSave[dateKey] = buildDynamicObjectDefinition(
              dateKey,
              options.period[dateKey] ?? null,
            ).values?.[dateKey];
          }
        }

        const saves = savesRef.current;
        const signature = JSON.stringify(valuesToSave);
        let saved = saves.get(key);
        if (saved?.value !== signature) {
          if (key === 'periodStart' || key === 'periodEnd') {
            saves.delete(key === 'periodStart' ? 'periodEnd' : 'periodStart');
          }
          saved = {
            value: signature,
            request: Promise.resolve(
              saveAdditionalFieldsFx({
                id: targetId,
                values: valuesToSave,
              }),
            ),
          };
          saves.set(key, saved);
        }
        try {
          await saved.request;
        } catch (error) {
          if (saves.get(key) === saved) saves.delete(key);
          throw error;
        }
        if (savesRef.current !== saves) return;

        if (
          options?.addToDictionary &&
          (key === 'agencyName' || key === 'clientName')
        ) {
          const stringValue = (value as string | null | undefined) ?? '';
          const trimmed = stringValue.trim();
          if (trimmed) {
            if (key === 'agencyName') {
              agencyDictionary.addWord(trimmed);
            } else {
              clientDictionary.addWord(trimmed);
            }
          }
        }
      } catch (error) {
        console.error(error);
        toast.error('Ошибка при сохранении поля', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        });
      }
    },
    [mainDynamicObjectId, values],
  );

  const debouncedSaveField = useCallback(
    (
      key: AdditionalFieldKey,
      value: AdditionalFieldsModel[AdditionalFieldKey],
    ) => {
      if (saveFieldTimeoutRef.current[key]) {
        clearTimeout(saveFieldTimeoutRef.current[key]);
      }

      saveFieldTimeoutRef.current[key] = setTimeout(() => {
        if (
          (key === 'agencyName' || key === 'clientName') &&
          !validForSaveRef.current[key]
        ) {
          delete saveFieldTimeoutRef.current[key];
          return;
        }
        saveField(key, value, { addToDictionary: false });
        delete saveFieldTimeoutRef.current[key];
      }, 1000);
    },
    [card.uuid, mainDynamicObjectId, saveField],
  );

  const clearSaveFieldTimeout = useCallback((key: string) => {
    const existing = saveFieldTimeoutRef.current[key];
    if (existing) {
      clearTimeout(existing);
      delete saveFieldTimeoutRef.current[key];
    }
  }, []);

  const setDictionaryFieldValidity = useCallback(
    (key: DictionaryFieldKey, valid: boolean) => {
      validForSaveRef.current[key] = valid;
    },
    [],
  );

  useEffect(() => {
    if (!canPersistDerived) return;

    const keys = Array.from(unsavedDerivedKeysRef.current);
    if (keys.length === 0) return;

    unsavedDerivedKeysRef.current.clear();
    const pendingValues = pendingDerivedValuesRef.current;
    pendingDerivedValuesRef.current = {};

    keys.forEach((key) => {
      const value = pendingValues[key];
      if (value !== undefined) {
        debouncedSaveField(key, value);
      }
    });
  }, [canPersistDerived, debouncedSaveField]);

  useEffect(() => {
    const nextValues = recalculateFinancialFields(values);

    const derivedKeys: AdditionalFieldKey[] = [
      'clientBudget',
      'returnDiscountSum',
      'adFeeSum',
      'payBudget',
      'budgetToPay',
    ];

    let hasChanges = false;

    derivedKeys.forEach((key) => {
      if (shouldSaveDerivedField(values[key], nextValues[key])) {
        hasChanges = true;
        if (canPersistDerived) {
          debouncedSaveField(key, nextValues[key]);
        } else {
          unsavedDerivedKeysRef.current.add(key);
          pendingDerivedValuesRef.current[key] = nextValues[key];
        }
      }
    });

    if (isFirstRecalcRunRef.current) {
      isFirstRecalcRunRef.current = false;
    }

    if (!hasChanges) {
      return;
    }

    setValues(nextValues);
  }, [
    values.billing,
    values.volume,
    values.clientCpm,
    values.returnDiscountPercent,
    values.adFeePercent,
    canPersistDerived,
    debouncedSaveField,
    setValues,
  ]);

  return {
    saveField,
    debouncedSaveField,
    clearSaveFieldTimeout,
    setDictionaryFieldValidity,
  };
}
